/**
 * When the "Book on the app" pop-up (computers) and banner (phones) may show
 * (owner, 2026-10-15):
 *
 * - after 20 seconds on the site, or on the second page of the session,
 *   whichever comes first;
 * - never on the booking flow, account, sign-in, bucket or application pages,
 *   nor on a lab;
 * - never while the cookie banner is still waiting for an answer, nor while a
 *   sheet or modal is open;
 * - never inside the mobile app's own web views;
 * - once closed, not again for 14 days.
 *
 * Pure functions here, so the rules are testable without rendering anything.
 */

export const APP_PROMO_DELAY_MS = 20_000;
export const APP_PROMO_SNOOZE_MS = 14 * 24 * 60 * 60 * 1000;

/** localStorage: when the guest last closed it (epoch ms). */
export const APP_PROMO_CLOSED_KEY = "mj_app_promo_closed_at";
/** sessionStorage: page views this session. */
export const APP_PROMO_VIEWS_KEY = "mj_app_promo_views";

/**
 * Path segments where the promo stays away: the booking flow from packages to
 * thank-you, stays checkout, transfers, account and sign-in, the bucket, the
 * application forms, and the chat pages (it would sit on the composer).
 */
const EXCLUDED_SEGMENTS = new Set([
  "packages",
  "details",
  "checkout",
  "thank-you",
  "booking",
  "bookings",
  "confirmation",
  "receipt",
  "bucket",
  "transport",
  "account",
  "sign-in",
  "sign-up",
  "forgot-password",
  "reset-password",
  "ambassadors",
  "creators",
  "become-a-partner",
  "partner",
  "partners",
  "ai-chat",
  "ai-travel-planner",
  "concierge",
  "chat",
  "app",
]);

export function isAppPromoExcludedPath(pathname: string): boolean {
  return pathname
    .split("/")
    .filter(Boolean)
    .some((seg) => EXCLUDED_SEGMENTS.has(seg) || seg.endsWith("-lab"));
}

/** Closed less than 14 days ago. A garbled value counts as never closed. */
export function isAppPromoSnoozed(closedAt: string | null, now: number): boolean {
  if (!closedAt) return false;
  const at = Number(closedAt);
  if (!Number.isFinite(at) || at <= 0) return false;
  return now - at < APP_PROMO_SNOOZE_MS;
}

/** The page counts as the second (or later) of the session. */
export function isSecondPageView(views: number): boolean {
  return views >= 2;
}

/** Inside the MaJourneys mobile app's web view (react-native-webview). */
export function isInAppWebView(win: { ReactNativeWebView?: unknown; navigator?: { userAgent?: string } }): boolean {
  if (win.ReactNativeWebView) return true;
  return /MaJourneysApp/i.test(win.navigator?.userAgent ?? "");
}

/** A sheet or modal is open somewhere on the page. */
export function isModalOpen(doc: Pick<Document, "querySelector">): boolean {
  return doc.querySelector('[aria-modal="true"]:not([aria-hidden="true"])') !== null;
}

export interface AppPromoGate {
  /** 20 seconds have passed, or this is the second page of the session. */
  armed: boolean;
  pathname: string;
  snoozed: boolean;
  /** The cookie banner has been answered (either way). */
  consentAnswered: boolean;
  inAppWebView: boolean;
  modalOpen: boolean;
}

export function shouldShowAppPromo(g: AppPromoGate): boolean {
  return (
    g.armed &&
    !g.snoozed &&
    g.consentAnswered &&
    !g.inAppWebView &&
    !g.modalOpen &&
    !isAppPromoExcludedPath(g.pathname)
  );
}

/* Storage, wrapped: private mode and blocked storage throw. */

export function readClosedAt(): string | null {
  try {
    return localStorage.getItem(APP_PROMO_CLOSED_KEY);
  } catch {
    return null;
  }
}

export function writeClosedAt(now: number): void {
  try {
    localStorage.setItem(APP_PROMO_CLOSED_KEY, String(now));
  } catch {
    // Private mode: it simply comes back next session.
  }
}

/** Counts this page view and returns the session's total so far. */
export function countPageView(): number {
  try {
    const n = (Number(sessionStorage.getItem(APP_PROMO_VIEWS_KEY)) || 0) + 1;
    sessionStorage.setItem(APP_PROMO_VIEWS_KEY, String(n));
    return n;
  } catch {
    return 1;
  }
}
